import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Container, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
    addDeliverectVirtualLocation,
    fetchBranches,
    getDeliverectVirtualLocations,
    removeDeliverectVirtualLocation,
    updateDeliverectVirtualLocation,
} from "../../../utils/apiCalls";
import { C, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { Badge, Loader, RCard } from "../../reporting/ReportingShared";
import Breadcrumb from "../../common/breadcrumb";

const ghostBtn = {
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    color: C.textMed,
    fontWeight: 600,
    fontSize: 12,
    padding: "6px 12px",
};
const primaryBtn = {
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
    border: "none",
    borderRadius: 8,
    color: "#fff",
    fontWeight: 700,
    fontSize: 12,
    padding: "8px 18px",
};
const dangerBtn = { ...ghostBtn, color: "#e74c3c", borderColor: "#f5c6cb" };

const th = {
    fontSize: 11,
    fontWeight: 700,
    color: C.textLight,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    padding: "10px 12px",
    borderBottom: `2px solid ${C.borderLight}`,
    textAlign: "left",
    whiteSpace: "nowrap",
};
const td = { fontSize: 13, color: C.text, padding: "12px", borderBottom: `1px solid ${C.borderLight}`, verticalAlign: "middle" };
const mono = { fontFamily: "SFMono-Regular, Menlo, monospace", fontSize: 12 };

const EMPTY_FORM = { externalLocationId: "", label: "", ParentBranchId: "", BranchId: "", active: true };

/**
 * Deliverect "virtual locations": Deliverect location IDs that have no branch of their own. Each
 * one uses a parent branch's menu (and registers with Deliverect through the parent's location),
 * sends its orders to an existing target branch, and its location name (`label`) travels with the
 * order so the POS and kitchen can tell brands apart. A tablet on the target branch can be set to
 * receive a location's orders alone from Devices → Order Routing.
 */
const DeliverectLocationsPage = () => {
    const userType = localStorage.getItem("userType");
    const merchantId = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;
    const hasMerchant = !!selectedMerchant && selectedMerchant !== "all";
    const readOnly = userType === "operations";

    const [isLoading, setIsLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [branches, setBranches] = useState([]);

    const [editing, setEditing] = useState(null); // null = closed, {} = new, row = edit
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const load = useCallback(async () => {
        setRows([]);
        setBranches([]);
        if (!hasMerchant) return;
        setIsLoading(true);
        try {
            const [list, branchRes] = await Promise.all([
                getDeliverectVirtualLocations(selectedMerchant),
                fetchBranches({ MerchantId: selectedMerchant }, true),
            ]);
            setRows(list);
            setBranches(branchRes?.data || []);
        } finally {
            setIsLoading(false);
        }
    }, [hasMerchant, selectedMerchant]);

    useEffect(() => {
        load();
    }, [load]);

    // An alias may point at a branch that has since been switched off; keep it selectable while editing.
    const branchOptions = useMemo(() => {
        const options = branches.map((b) => ({ id: b.id, name: b.name }));
        const current = editing?.targetBranch;
        if (current && !options.some((o) => o.id === current.id)) {
            options.push({ id: current.id, name: `${current.name} (inactive)` });
        }
        return options;
    }, [branches, editing]);

    // Registration goes through the parent's own Deliverect location, so only linked branches qualify.
    const parentOptions = useMemo(() => {
        const options = branches.filter((b) => b.externalLocationId).map((b) => ({ id: b.id, name: b.name }));
        const current = editing?.parentBranch;
        if (current && !options.some((o) => o.id === current.id)) {
            options.push({ id: current.id, name: `${current.name}${current.active ? "" : " (inactive)"}` });
        }
        return options;
    }, [branches, editing]);

    const openNew = () => {
        setForm(EMPTY_FORM);
        setEditing({});
    };
    const openEdit = (row) => {
        setForm({
            externalLocationId: row.externalLocationId,
            label: row.label,
            ParentBranchId: row.ParentBranchId ? String(row.ParentBranchId) : "",
            BranchId: String(row.BranchId),
            active: !!row.active,
        });
        setEditing(row);
    };
    const closeForm = () => {
        if (saving) return;
        setEditing(null);
    };

    const formError = (() => {
        if (!form.externalLocationId.trim()) return "Deliverect location ID is required";
        if (!form.label.trim()) return "Location name is required";
        if (!form.ParentBranchId) return "Choose the parent branch whose menu it uses";
        if (!form.BranchId) return "Choose the branch that receives the orders";
        return null;
    })();

    const handleSave = async () => {
        if (formError) return;
        setSaving(true);
        try {
            const payload = {
                externalLocationId: form.externalLocationId.trim(),
                label: form.label.trim(),
                ParentBranchId: Number(form.ParentBranchId),
                BranchId: Number(form.BranchId),
                active: form.active,
            };
            const saved = editing?.id
                ? await updateDeliverectVirtualLocation(editing.id, payload)
                : await addDeliverectVirtualLocation({ ...payload, merchantId: Number(selectedMerchant) });
            if (saved) {
                setEditing(null);
                await load();
            }
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (row) => {
        const saved = await updateDeliverectVirtualLocation(row.id, { active: !row.active });
        if (saved) await load();
    };

    const confirmDelete = async () => {
        const ok = await removeDeliverectVirtualLocation(deleteTarget.id);
        setDeleteTarget(null);
        if (ok) await load();
    };

    const renderTable = () => {
        if (!hasMerchant) {
            return <p style={{ textAlign: "center", padding: 40, color: C.textLight, margin: 0 }}>Select a merchant</p>;
        }
        if (isLoading) return <Loader />;
        if (!rows.length) {
            return (
                <p style={{ textAlign: "center", padding: 40, color: C.textLight, margin: 0 }}>
                    No virtual locations yet. Orders from a Deliverect location ID that matches no branch are rejected
                    until it is added here.
                </p>
            );
        }

        return (
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr>
                            <th style={th}>Location name</th>
                            <th style={th}>Deliverect location ID</th>
                            <th style={th}>Menu from</th>
                            <th style={th}>Orders go to</th>
                            <th style={th}>Tablet</th>
                            <th style={th}>Status</th>
                            {!readOnly && <th style={{ ...th, textAlign: "right" }}>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const branchOff = row.targetBranch && !row.targetBranch.active;
                            return (
                                <tr key={row.id} className="rpt-row">
                                    <td style={{ ...td, fontWeight: 600 }}>{row.label}</td>
                                    <td style={{ ...td, ...mono }}>{row.externalLocationId}</td>
                                    <td style={td}>
                                        {row.parentBranch ? (
                                            <>
                                                {row.parentBranch.name}
                                                {!row.parentBranch.externalLocationId && (
                                                    <div style={{ fontSize: 11, color: C.orange, marginTop: 2 }}>
                                                        Parent has no Deliverect location ID, so registration will fail
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <span style={{ fontSize: 12, color: C.orange, fontWeight: 600 }}>
                                                Parent branch not set, so Deliverect registration will fail
                                            </span>
                                        )}
                                    </td>
                                    <td style={td}>
                                        {row.targetBranch?.name || `Branch #${row.BranchId}`}
                                        {branchOff && (
                                            <div style={{ fontSize: 11, color: C.orange, marginTop: 2 }}>
                                                Branch is inactive, so these orders are rejected
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ ...td, color: row.routedToDevice ? C.text : C.textLight }}>
                                        {row.routedToDevice?.name || "Primary / all tablets"}
                                    </td>
                                    <td style={td}>
                                        {row.active
                                            ? <Badge color={C.coral} bg={C.coralLight}>Active</Badge>
                                            : <Badge color={C.textMed} bg={C.bg}>Off</Badge>}
                                    </td>
                                    {!readOnly && (
                                        <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                                            <Button size="sm" style={{ ...ghostBtn, marginRight: 6 }} onClick={() => openEdit(row)}>
                                                Edit
                                            </Button>
                                            <Button size="sm" style={{ ...ghostBtn, marginRight: 6 }} onClick={() => toggleActive(row)}>
                                                {row.active ? "Switch off" : "Switch on"}
                                            </Button>
                                            <Button size="sm" style={dangerBtn} onClick={() => setDeleteTarget(row)}>
                                                Delete
                                            </Button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <Fragment>
            <style>{reportingGlobalCSS}</style>
            <Breadcrumb title="Deliverect Locations" parent="Merchants" />
            <Container fluid className="rpt-wrap">
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Deliverect virtual locations</div>
                        <div style={{ fontSize: 12, color: C.textLight, maxWidth: 720 }}>
                            Deliverect locations that are not branches here. Each uses a parent branch&apos;s menu and registers
                            through it; its orders are created on the chosen target branch, and the location name is saved on each order. To send a location&apos;s orders to one tablet, use Devices → Order Routing.
                        </div>
                    </div>
                    {!hasMerchant && (
                        <span style={{ color: C.orange, fontWeight: 600, fontSize: 13 }}>Please select a merchant from the dropdown</span>
                    )}
                    <div style={{ flex: 1 }} />
                    {hasMerchant && !readOnly && (
                        <Button size="sm" style={primaryBtn} onClick={openNew}>
                            + Add location
                        </Button>
                    )}
                </div>

                <RCard>{renderTable()}</RCard>
            </Container>

            <Modal isOpen={!!editing} toggle={closeForm} centered>
                <ModalHeader toggle={closeForm}>{editing?.id ? "Edit virtual location" : "Add virtual location"}</ModalHeader>
                <ModalBody>
                    <div style={{ marginBottom: 14 }}>
                        <Label style={{ fontSize: 12, fontWeight: 600 }}>Deliverect location ID</Label>
                        <Input
                            value={form.externalLocationId}
                            onChange={(e) => setForm({ ...form, externalLocationId: e.target.value })}
                            placeholder="e.g. 64f1c2a9e0b7a1d2c3e4f5a6"
                            style={mono}
                        />
                        <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>
                            Copy it from the location in Deliverect. It cannot match a branch&apos;s location ID.
                        </div>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                        <Label style={{ fontSize: 12, fontWeight: 600 }}>Location name</Label>
                        <Input
                            value={form.label}
                            maxLength={120}
                            onChange={(e) => setForm({ ...form, label: e.target.value })}
                            placeholder="e.g. Burger Bros – Olaya"
                        />
                        <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>
                            Saved on every order from this location, so you can tell its orders apart.
                        </div>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                        <Label style={{ fontSize: 12, fontWeight: 600 }}>Menu from (parent branch)</Label>
                        <Input
                            type="select"
                            value={form.ParentBranchId}
                            onChange={(e) => setForm({ ...form, ParentBranchId: e.target.value })}
                        >
                            <option value="">Select the parent branch</option>
                            {parentOptions.map((b) => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                        </Input>
                        <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>
                            This location uses the parent&apos;s menu, and Deliverect registers it through the parent&apos;s location.
                            Only branches linked to a Deliverect location are listed.
                        </div>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                        <Label style={{ fontSize: 12, fontWeight: 600 }}>Orders go to branch</Label>
                        <Input type="select" value={form.BranchId} onChange={(e) => setForm({ ...form, BranchId: e.target.value })}>
                            <option value="">Select a branch</option>
                            {branchOptions.map((b) => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                        </Input>
                        {editing?.id && editing.routedToDevice && String(editing.BranchId) !== String(form.BranchId) && (
                            <div style={{ fontSize: 11, color: C.orange, marginTop: 4 }}>
                                Moving it removes it from {editing.routedToDevice.name}&apos;s order routing.
                            </div>
                        )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Input
                            type="checkbox"
                            id="dvl-active"
                            checked={form.active}
                            onChange={(e) => setForm({ ...form, active: e.target.checked })}
                            style={{ margin: 0, position: "static" }}
                        />
                        <Label for="dvl-active" style={{ margin: 0, fontSize: 13 }}>
                            Active: accept orders from this location
                        </Label>
                    </div>
                </ModalBody>
                <ModalFooter>
                    {formError && <span style={{ fontSize: 12, color: C.textLight, marginRight: "auto" }}>{formError}</span>}
                    <Button style={ghostBtn} onClick={closeForm} disabled={saving}>
                        Cancel
                    </Button>
                    <Button style={{ ...primaryBtn, opacity: formError ? 0.5 : 1 }} onClick={handleSave} disabled={saving || !!formError}>
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={!!deleteTarget} toggle={() => setDeleteTarget(null)} centered>
                <ModalHeader toggle={() => setDeleteTarget(null)}>Delete virtual location</ModalHeader>
                <ModalBody>
                    Delete <strong>{deleteTarget?.label}</strong> ({deleteTarget?.externalLocationId})? New orders from this
                    Deliverect location will be rejected. Past orders keep their location name.
                </ModalBody>
                <ModalFooter>
                    <Button style={ghostBtn} onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button style={{ ...primaryBtn, background: "#e74c3c" }} onClick={confirmDelete}>Delete</Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default DeliverectLocationsPage;
